import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Heart, Share2, Flag, Trash2, Eye, EyeOff, MoreHorizontal, Lightbulb, MessageCircle, Send, Loader2, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { commentsApi, translationApi } from '../lib/api';
import { toast } from 'sonner';

// Fullscreen image viewer
const ImageViewer = ({ src, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center" onClick={onClose}>
    <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/20 rounded-full z-10">
      <X className="w-6 h-6 text-white" />
    </button>
    <img src={src} alt="" className="max-w-full max-h-full object-contain p-4" onClick={(e) => e.stopPropagation()} />
  </motion.div>
);
import { TranslatableComment } from './TranslatableComment';

// Language display names for "See original" label
const LANG_NAMES = {
  en: 'English', es: 'Español', fr: 'Français', de: 'Deutsch',
  it: 'Italiano', pt: 'Português', hi: 'हिन्दी', bn: 'বাংলা',
  zh: '中文', pa: 'ਪੰਜਾਬੀ'
};

export const GemCard = ({ gem, anonId, userId, userName, onLike, onShare, onReport, onDelete, onMessageUser }) => {
  const { t, language } = useLanguage();
  const [showActions, setShowActions] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [viewImage, setViewImage] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [showFullscreenPhoto, setShowFullscreenPhoto] = useState(false);
  
  // Combined owner ID (use user ID if logged in, otherwise anonId)
  const ownerId = userId || anonId;
  const ownerName = userName || 'Anonymous';
  
  // Translation state
  const [showOriginal, setShowOriginal] = useState(false);
  const [translatedText, setTranslatedText] = useState(null);
  const [translatedInsight, setTranslatedInsight] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState(null);
  const [sourceLang, setSourceLang] = useState(gem.lang || 'en');
  
  const isOwner = gem.author_anon_id === anonId;
  const isLiked = (gem.liked_by || []).includes(anonId);
  
  // Check if translation is needed (gem lang differs from user lang)
  // Default to user's language if gem.lang is not set (meaning it was created in user's current language)
  const gemLang = gem.lang || language;
  const needsTranslation = gemLang !== language;

  const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };
  
  // Fetch translation when gem loads or language changes
  useEffect(() => {
    if (needsTranslation && !translatedText && !isTranslating && !translationError) {
      fetchTranslation();
    }
  }, [gem.id, language, needsTranslation]);
  
  // Reset translation when language changes
  useEffect(() => {
    setTranslatedText(null);
    setTranslatedInsight(null);
    setTranslationError(null);
    setShowOriginal(false);
  }, [language]);
  
  const fetchTranslation = async () => {
    setIsTranslating(true);
    setTranslationError(null);
    try {
      const response = await translationApi.translateGem(gem.id, language);
      const data = response.data;
      
      if (data.is_original) {
        // Same language, no translation needed
        setTranslatedText(null);
      } else {
        setTranslatedText(data.translated_text);
        setTranslatedInsight(data.translated_insight);
        setSourceLang(data.source_lang || gem.lang || 'en');
      }
    } catch (err) {
      console.error('Translation error:', err);
      const errorMsg = err.response?.data?.detail || 'Translation unavailable';
      setTranslationError(errorMsg);
      // Don't show toast for service not configured - that's expected without API key
      if (!errorMsg.includes('not configured')) {
        toast.error(t('translation_error') || 'Translation failed');
      }
    } finally {
      setIsTranslating(false);
    }
  };
  
  // Get display text (translated or original)
  const displayText = (!needsTranslation || showOriginal || !translatedText) ? gem.text : translatedText;
  const displayInsight = (!needsTranslation || showOriginal || !translatedInsight) ? gem.insight : translatedInsight;

  // Load comments count on mount, full list when section is opened
  useEffect(() => {
    loadComments();
  }, [gem.id]);

  useEffect(() => {
    if (showComments && comments.length === 0) {
      loadComments();
    }
  }, [showComments]);

  const loadComments = async () => {
    setLoadingComments(true);
    try {
      const res = await commentsApi.getComments(gem.id);
      setComments(res.data.comments || []);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    
    try {
      const res = await commentsApi.createComment(
        gem.id,
        ownerId,
        ownerName,
        commentText,
        language // Pass current language
      );
      setComments([res.data, ...comments]);
      setCommentText('');
      toast.success(t('comment_added') || 'Comment added!');
    } catch (err) {
      toast.error(t('failed_add_comment') || 'Failed to add comment');
    }
  };

  const handleToggleCommentLike = async (commentId) => {
    try {
      const res = await commentsApi.toggleLike(commentId, ownerId);
      setComments(comments.map(c => 
        c.id === commentId 
          ? { ...c, likes: res.data.action === 'liked' 
              ? [...(c.likes || []), ownerId] 
              : (c.likes || []).filter(id => id !== ownerId),
              likes_count: res.data.likes_count
            }
          : c
      ));
    } catch (err) {
      toast.error(t('failed_like_comment') || 'Failed to like comment');
    }
  };

  const handleReportComment = async (commentId) => {
    try {
      await commentsApi.reportComment(commentId, ownerId, 'Inappropriate content');
      toast.success(t('comment_reported') || 'Comment reported');
    } catch (err) {
      toast.error(t('failed_report_comment') || 'Failed to report comment');
    }
  };

  const handleEditComment = async (commentId, newText) => {
    try {
      const res = await commentsApi.updateComment(commentId, ownerId, newText);
      setComments(comments.map(c => 
        c.id === commentId ? { ...c, text: res.data.text, edited_at: res.data.edited_at } : c
      ));
      toast.success(t('comment_updated') || 'Comment updated');
    } catch (err) {
      toast.error(t('comment_update_failed') || 'Failed to update comment');
      throw err;
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await commentsApi.deleteComment(commentId, ownerId);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success(t('comment_deleted') || 'Comment deleted');
    } catch (err) {
      toast.error(t('comment_delete_failed') || 'Failed to delete comment');
    }
  };

  return (
    <div
      className="rounded-[var(--radius)] border border-border bg-card shadow-[0_8px_24px_-18px_hsl(var(--shadow-color)/0.55)] p-4"
      data-testid={`gem-card-${gem.id}`}
    >
      {/* Fullscreen Photo Modal */}
      <AnimatePresence>
        {showFullscreenPhoto && gem.author_photo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setShowFullscreenPhoto(false)}
          >
            <button
              onClick={() => setShowFullscreenPhoto(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="close-fullscreen-photo"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <motion.img
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              src={gem.author_photo}
              alt={gem.author_name || 'Profile'}
              className="max-w-full max-h-[80vh] rounded-xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-8 text-white text-lg font-medium">
              {gem.author_name || 'Anonymous'}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fullscreen Image Viewer for media */}
      <AnimatePresence>
        {viewImage && <ImageViewer src={viewImage} onClose={() => setViewImage(null)} />}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => gem.author_photo && setShowFullscreenPhoto(true)}
            className={`w-8 h-8 rounded-full bg-accent flex items-center justify-center overflow-hidden ${gem.author_photo ? 'cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all' : ''}`}
            disabled={!gem.author_photo}
            data-testid={`gem-avatar-${gem.id}`}
          >
            {gem.author_photo ? (
              <img src={gem.author_photo} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs font-medium text-accent-foreground">
                {(gem.author_name || 'A').charAt(0).toUpperCase()}
              </span>
            )}
          </button>
          <div>
            <button 
              onClick={() => gem.author_user_id && onMessageUser && onMessageUser(gem.author_user_id, gem.author_name)}
              className={`text-xs font-medium text-foreground ${gem.author_user_id && onMessageUser ? 'hover:text-primary hover:underline cursor-pointer' : ''}`}
              disabled={!gem.author_user_id || !onMessageUser}
              data-testid={`gem-author-${gem.id}`}
            >
              {gem.author_name || 'Anonymous'}
            </button>
            <p className="text-[10px] text-muted-foreground">{timeAgo(gem.created_at)}</p>
          </div>
        </div>
        <button
          onClick={() => setShowActions(!showActions)}
          className="p-1 rounded-lg hover:bg-accent transition-colors duration-150"
          data-testid={`gem-more-${gem.id}`}
        >
          <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Verse Reference Badge */}
      {gem.verse_ref && (
        <div className="mt-2.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            {gem.verse_ref}
          </span>
        </div>
      )}

      {/* Translation loading state */}
      {isTranslating && (
        <div className="mt-2.5 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>{t('translating') || 'Translating...'}</span>
        </div>
      )}

      {/* Gem Text */}
      {gem.text && (
        <div className="mt-2.5">
          <p className={`text-sm leading-6 text-foreground/90 italic ${isTranslating ? 'opacity-50' : ''}`} data-testid={`gem-text-${gem.id}`}>
            "{displayText}"
          </p>
        </div>
      )}

      {/* Insight */}
      {(gem.insight || displayInsight) && (
        <div className="mt-2.5 pl-3 border-l-2 border-primary/30">
          <div className="flex items-center gap-1 mb-1">
            <Lightbulb className="w-3 h-3 text-primary/70" />
            <span className="text-[10px] font-medium text-primary/70 uppercase tracking-wide">{t('insight_label')}</span>
          </div>
          <p className={`text-sm leading-6 text-foreground/80 ${isTranslating ? 'opacity-50' : ''}`} data-testid={`gem-insight-${gem.id}`}>
            {displayInsight}
          </p>
        </div>
      )}

      {/* Media attachments */}
      {gem.media && gem.media.length > 0 && (
        <div className={`mt-3 ${gem.media.length > 1 ? 'grid grid-cols-2 gap-2' : ''}`}>
          {gem.media.map((m, i) => (
            <div key={i} className="rounded-xl overflow-hidden border border-border/50">
              {m.type === 'image' ? (
                <img src={m.data} alt={m.caption || ''} className="w-full max-h-64 object-cover cursor-pointer"
                  onClick={() => setViewImage(m.data)} />
              ) : m.type === 'video' && m.data.includes('youtu') ? (
                <iframe
                  src={m.data.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                  className="w-full h-44" frameBorder="0" allowFullScreen
                  title={m.caption || 'Video'} />
              ) : m.type === 'video' ? (
                <a href={m.data} target="_blank" rel="noopener noreferrer"
                  className="block p-3 text-xs text-primary hover:underline truncate">{m.data}</a>
              ) : null}
              {m.caption && (
                <p className="px-2 py-1.5 text-[10px] text-muted-foreground bg-accent/30">{m.caption}</p>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* See Original / See Translation Toggle */}
      {needsTranslation && translatedText && !isTranslating && (
        <button
          onClick={() => setShowOriginal(!showOriginal)}
          className="mt-2 flex items-center gap-1.5 text-xs text-primary/70 hover:text-primary transition-colors"
          data-testid={`gem-toggle-original-${gem.id}`}
        >
          {showOriginal ? (
            <>
              <EyeOff className="w-3 h-3" />
              <span>{t('see_translation') || 'See translation'}</span>
            </>
          ) : (
            <>
              <Eye className="w-3 h-3" />
              <span>{t('see_original') || 'See original'} ({LANG_NAMES[sourceLang] || sourceLang})</span>
            </>
          )}
        </button>
      )}

      {/* Actions row */}
      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {/* Like */}
          <motion.button
            whileTap={{ scale: 1.15 }}
            onClick={() => onLike(gem.id)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors duration-200 ${
              isLiked
                ? 'text-primary bg-primary/10'
                : 'text-muted-foreground hover:bg-accent'
            }`}
            data-testid={`gem-like-${gem.id}`}
          >
            <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-primary' : ''}`} />
            <span>{gem.likes_count || 0}</span>
          </motion.button>

          {/* Comments - moved next to Like */}
          <button
            onClick={() => setShowComments(!showComments)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors duration-200 ${
              showComments
                ? 'text-primary bg-primary/10'
                : 'text-muted-foreground hover:bg-accent'
            }`}
            data-testid={`gem-comments-${gem.id}`}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            <span>{comments.length}</span>
          </button>

          {/* Share */}
          <button
            onClick={() => onShare(gem)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-muted-foreground hover:bg-accent transition-colors duration-200"
            data-testid={`gem-share-${gem.id}`}
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>

          {/* Report */}
          <button
            onClick={() => onReport(gem.id)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-muted-foreground hover:bg-accent transition-colors duration-200"
            data-testid={`gem-report-${gem.id}`}
          >
            <Flag className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Delete action (owner only) */}
        {showActions && isOwner && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-1"
          >
            <button
              onClick={() => { onDelete(gem.id); setShowActions(false); }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-destructive hover:bg-destructive/10 transition-colors duration-200"
              data-testid={`gem-delete-${gem.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('delete')}
            </button>
          </motion.div>
        )}
      </div>

      {/* Comments Section */}
      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 border-t border-border pt-4"
          >
            {/* Comment Input */}
            <div className="mb-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  placeholder="Add a comment..."
                  className="flex-1 px-3 py-2 text-sm bg-accent/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  maxLength={500}
                  data-testid={`comment-input-${gem.id}`}
                />
                <button
                  onClick={handleAddComment}
                  disabled={!commentText.trim()}
                  className="px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid={`comment-submit-${gem.id}`}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Comments List */}
            {loadingComments ? (
              <div className="text-center py-4">
                <div className="inline-block w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : comments.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No comments yet. Be the first to comment!</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {comments.map((comment) => (
                  <TranslatableComment
                    key={comment.id}
                    comment={comment}
                    anonId={ownerId}
                    onLike={handleToggleCommentLike}
                    onReport={handleReportComment}
                    onEdit={handleEditComment}
                    onDelete={handleDeleteComment}
                    timeAgo={timeAgo}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GemCard;
